"use client";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import React from "react";
import { cn } from "~/utils/fontsConfig";

const styleClassNames = {
    shared: `
        w-max
        h-full
        rounded-full
        shadow-lg
        gap-2
        inline-flex 
        items-center 
        justify-center 
        font-normal 
        ring-offset-background
        ring-v3-N-400
        transition-colors 
        focus-visible:outline-none 
        focus-visible:ring-2 
        focus-visible:ring-ring 
        focus-visible:ring-offset-2 
        disabled:cursor-not-allowed
        disabled:opacity-50
        disabled:text-black
        disabled:bg-v3-N-300
    `,
    default: `
        text-black
        text-sm
        font-medium
        leading-1
        break-words
        min-w-max
        bg-v3-primary
        
        hover:ring-2
        hover:white
        disabled:bg-v3-N-200
        disabled:text-v3-N-300
    `,
    secondary: `
        text-black
        text-sm
        font-medium
        leading-1
        break-words
        bg-white
        hover:bg-v3-N-200
        disabled:bg-v3-N-200
        disabled:text-v3-N-300
    `,
    tertiary: `
        text-white
        text-sm
        font-medium
        leading-1
        break-words
        bg-transparent
        border-[2px]
        border-white
        hover:bg-white
        hover:text-black
        disabled:text-v3-N-200
        disabled:border-v3-N-300
    `,
    destructive: `
        bg-destructive 
        text-black
        hover:bg-destructive/90
    `,
    primaryLink: `
        text-v3-primary
        justify-start 
        w-max
        p-0 
        m-0
        hover:underline
        bg-transparent
        shadow-none
    `,
    secondaryLink: `
        text-white
        underline
        w-max
        justify-start 
        p-0 
        hover:text-v3-N-200
    `,
    info: `
        bg-[#313715]
        hover:bg-[#455e10]
        text-v3-primary
        font-medium
    `,
    ghost: `
        bg-transparent
    `,
    selectedGhost: `
        bg-v4-NBADGE rounded-[10px]
    `,
    slider: `
        bg-transparent
        text-white
        rounded-none
        disabled:invisible
        disabled:pointer-events-none
    `,
    tabPrimary: `
        bg-transparent 
        text-v3-light-gray
        
        text-sm
        
        leading-1
        
        break-words
        min-w-max
        
        hover:ring-2
        hover:white
        disabled:bg-v3-N-200
        disabled:text-v3-N-300
        
        rounded
    `,
    admin: `
        bg-v3-S500
        disabled:bg-v3-S75
        
        hover:bg-v3-S400
    `,
    badge: `
        bg-[#36373B]
    `,
};

export const buttonVariants = cva(styleClassNames.shared, {
    variants: {
        variant: {
            default: styleClassNames.default,
            secondary: styleClassNames.secondary,
            tertiary: styleClassNames.tertiary,
            primaryLink: styleClassNames.primaryLink,
            secondaryLink: styleClassNames.secondaryLink,
            destructive: styleClassNames.destructive,
            info: styleClassNames.info,
            ghost: styleClassNames.ghost,
            selectedGhost: styleClassNames.selectedGhost,
            slider: styleClassNames.slider,
            link: styleClassNames.ghost,
            tabPrimary: styleClassNames.tabPrimary,
            admin: styleClassNames.admin,
            badge: styleClassNames.badge,
        },
        size: {
            default: "h-9 px-4 py-2",
            sm: "h-7 px-3",
            md: "h-7 rounded-xl px-3",
            xs: "h-5 rounded-md px-2",
            lg: "h-11 rounded-md px-8",
            icon: "h-10 w-10",
            breadcrumb: "p-0 text-2xl font-semibold",
            tab: "h-5 p-3",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(
                    buttonVariants({ variant, size, className }),
                    isLoading ? "opacity-50" : "",
                )}
                disabled={isLoading || props.disabled}
                ref={ref}
                {...props}
            />
        );
    },
);

Button.displayName = "Button";
